/**
 * The landing after Review created the brand (spec §2.2, `done`).
 *
 * Not a form: the channel exists and is active by the time this renders, so
 * the address is a fact rather than a field, and the three buttons only choose
 * where the operator lands — a sample video, their own clip (the upload lane),
 * or the library.
 *
 * `onFinish` still creates when the brand somehow ISN'T there yet (a Create
 * that failed, then a rail click to this screen): the shell decides, because
 * only the shell knows whether the POST already happened. That is why the
 * buttons carry a busy state at all.
 */
import { ArrowRight, Compass, Loader2, Library, Upload } from "lucide-react";
import type { BrandSetupState } from "../../../lib/brandSetup/state";
import { effectiveFontNames } from "../../../lib/brandSetup/state";
import { MONO_LABEL, primaryButton, secondaryButton } from "../ui";

/** Where the operator is heading. `false` = the library; `tour` = the
 *  guided walk-through (the app starts it on New job after the reload). */
export type FinishIntent = "sample" | "upload" | "tour" | false;

function Row({ label, children }: { label: string; children: React.ReactNode }): React.JSX.Element {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 14, padding: "11px 0", borderBottom: "1px solid rgba(255,255,255,.06)" }}>
      <span style={{ ...MONO_LABEL, width: 96, flexShrink: 0 }}>{label}</span>
      <span style={{ fontSize: 14, color: "var(--ql-fg)", minWidth: 0, flex: 1 }}>{children}</span>
    </div>
  );
}

export function DoneStep(props: {
  state: BrandSetupState;
  slug: string;
  submitting: boolean;
  error: string | null;
  /** False when this deployment has no sample catalog — the CTA that would
   *  dead-end on "No samples yet" is not offered at all (H5). */
  samplesOn?: boolean;
  onFinish: (intent: FinishIntent) => void;
}): React.JSX.Element {
  const { state, slug, submitting, error, samplesOn = true } = props;
  const { display, body } = effectiveFontNames(state);
  return (
    <div style={{ display: "grid", gap: 26 }}>
      <div>
        <Row label="Name">{state.brandName.trim()}</Row>
        <Row label="Address">
          <span style={{ fontFamily: "var(--ql-font-mono)", fontSize: 13, color: "var(--ql-fg-muted)" }}>{slug}</span>
        </Row>
        {/* The SAME labels Review used, in the same words: two names for one
            thing across two adjacent screens is how a product stops sounding
            like one product (QA 2026-09-16, m3). */}
        <Row label="Language">{state.language.displayName}</Row>
        <Row label="Font">
          {display} for the speaker&rsquo;s name, {body} for captions
        </Row>
        <Row label="Colors">
          <span style={{ display: "inline-flex", gap: 6, alignItems: "center" }}>
            {(["accent", "ink", "paper", "muted"] as const).map((k) => (
              <span
                key={k}
                title={`${k} · ${state.colors[k]}`}
                style={{ width: 18, height: 18, borderRadius: 6, background: state.colors[k], border: "1px solid rgba(255,255,255,.18)" }}
              />
            ))}
          </span>
        </Row>
        {state.studyId && <Row label="Read from">{state.sources.length} source{state.sources.length === 1 ? "" : "s"} you gave us</Row>}
      </div>

      {error && <div style={{ fontSize: 13.5, color: "var(--ql-danger)", lineHeight: 1.6 }}>{error}</div>}

      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
        {samplesOn && (
          <button type="button" disabled={submitting} onClick={() => props.onFinish("sample")} style={primaryButton(submitting)}>
            {submitting ? <Loader2 size={16} className="ql-spin" /> : <ArrowRight size={16} />}
            Try a sample video
          </button>
        )}
        {/* The operator who already has a clip should not have to go looking
            for the upload lane — it is the second CTA, not a menu item. */}
        <button
          type="button"
          disabled={submitting}
          onClick={() => props.onFinish("upload")}
          style={samplesOn ? secondaryButton(submitting) : primaryButton(submitting)}
        >
          <Upload size={15} />
          Use my own clip
        </button>
        {/* The guided tour (spec §2.6): walks the build of one video, from
            New job to Schedule post, skippable at every step. */}
        <button type="button" disabled={submitting} onClick={() => props.onFinish("tour")} style={secondaryButton(submitting)}>
          <Compass size={15} />
          Show me around
        </button>
        <button type="button" disabled={submitting} onClick={() => props.onFinish(false)} style={secondaryButton(submitting)}>
          <Library size={15} />
          Go to the library
        </button>
      </div>
    </div>
  );
}


